import { useJsonLd } from "../lib/seo";

/**
 * Site-wide combined structured-data `@graph` — the single authoritative
 * JSON-LD entity graph for jyotish-astro.com, provided by the site owner
 * 2026-07-27. Mounted once at the app root (see App.tsx) so it renders on
 * EVERY route, not just one page: an `@graph` cross-links entities by
 * `@id` (Organization ⇄ Person ⇄ ProfessionalService ⇄ SoftwareApplication
 * ⇄ Article), and search engines resolve those `@id` references within a
 * single page's structured data, so keeping the whole graph present on all
 * routes lets any page's own page-specific JSON-LD (e.g. Info.tsx's
 * Person, Angebote.tsx's Products) reference these shared `@id`s and be
 * understood as one connected knowledge graph.
 *
 * Uses `useJsonLd` (lib/seo.ts) with a stable id so it's injected once and
 * de-duplicated across re-renders — and, unlike useSEO, it only *adds* a
 * <script type="application/ld+json"> block and never touches
 * title/description/OG, so it can't race with I18nProvider's Home meta
 * ownership.
 *
 * This is intentionally the richer replacement for the minimal
 * Organization/WebSite blocks that were previously hard-coded in
 * index.html — those stay for no-JS crawlers/social-unfurlers (first
 * paint, no JS execution), and this graph layers the fuller E-E-A-T
 * entities (author credentials, service catalog, calculator provenance,
 * cornerstone Article) on top for JS-executing crawlers like Googlebot.
 */
const SITE_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://jyotish-astro.com/#organization",
      name: "Jyotish Astro",
      url: "https://jyotish-astro.com",
      logo: "https://jyotish-astro.com/assets/logo.png",
      sameAs: [
        "https://www.wikidata.org/wiki/Q838421",
        "https://de.wikipedia.org/wiki/Vedische_Astrologie",
      ],
      knowsAbout: [
        {
          "@type": "DefinedTerm",
          name: "Vedische Astrologie",
          sameAs: "https://en.wikipedia.org/wiki/Vedic_astrology",
        },
        {
          "@type": "DefinedTerm",
          name: "Siderischer Tierkreis",
          sameAs: "https://en.wikipedia.org/wiki/Sidereal_and_tropical_astrology",
        },
        {
          "@type": "DefinedTerm",
          name: "Ayanamsha (Lahiri)",
          sameAs: "https://en.wikipedia.org/wiki/Ayanamsa",
        },
        {
          "@type": "DefinedTerm",
          name: "Nakshatra",
          sameAs: "https://en.wikipedia.org/wiki/Nakshatra",
        },
      ],
    },
    {
      "@type": "Person",
      "@id": "https://jyotish-astro.com/#author",
      name: "Nastasja Berwing",
      alternateName: "Ana",
      jobTitle:
        "Zertifizierte Vedische Astrologin (Fellow of the Institute of Vedic Astrology)",
      alumniOf: [
        {
          "@type": "EducationalOrganization",
          name: "Institute of Vedic Astrology",
          url: "https://www.ivaindia.com/",
        },
        { "@type": "EducationalOrganization", name: "Astrovedic Akademie" },
      ],
      sameAs: ["https://jyotish-astro.com/about"],
      worksFor: { "@id": "https://jyotish-astro.com/#organization" },
    },
    {
      "@type": "ProfessionalService",
      "@id": "https://jyotish-astro.com/#consultation-service",
      name: "Vedische Astrologie Beratung & Horoskop Reading",
      alternateName: "Vedic Astrology Reading & Consultation Europe",
      provider: { "@id": "https://jyotish-astro.com/#organization" },
      areaServed: ["DE", "AT", "CH", "Europe", "Worldwide"],
      availableLanguage: ["German", "English"],
      description:
        "Individuelle und professionelle Vedische Astrologie Beratung (Jyotish Reading) für persönliche Orientierung, Dasha-Analysen und Lebensfragen. Verfügbar online für ganz Europa und weltweit.",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Jyotish Beratungsangebote",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Erstberatung & Geburtshoroskop-Analyse (Natal Chart Reading)",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Jahresprognose & Vimshottari Dasha Analyse",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Partnerschaftsanalyse (Synastrie & Ashta Kuta)",
            },
          },
        ],
      },
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://jyotish-astro.com/#calculator",
      name: "Jyotish Vedischer Horoskop Rechner",
      operatingSystem: "All",
      applicationCategory: "DesignApplication",
      isBasedOn: [
        "https://www.astro.com/swisseph/",
        "https://ssd.jpl.nasa.gov/",
      ],
      offers: { "@type": "Offer", price: "0.00", priceCurrency: "EUR" },
    },
    {
      "@type": "Article",
      "@id": "https://jyotish-astro.com/vedische-sternzeichen/#article",
      isPartOf: {
        "@type": "WebPage",
        "@id": "https://jyotish-astro.com/vedische-sternzeichen/",
      },
      headline:
        "Die 12 Vedischen Sternzeichen (Rashis) im siderischen Tierkreis",
      description:
        "Umfassender Leitfaden zu den 12 vedischen Sternzeichen, ihren Unterschieden zum tropischen System und der Berechnung des Aszendenten.",
      citation: [
        "https://www.wisdomlib.org/hinduism/book/brihat-parasara-hora-sastra",
        "https://en.wikipedia.org/wiki/Ayanamsa",
      ],
      author: { "@id": "https://jyotish-astro.com/#author" },
      publisher: { "@id": "https://jyotish-astro.com/#organization" },
      inLanguage: "de-DE",
    },
  ],
};

/**
 * Renders nothing to the DOM tree; its only job is to inject the site-wide
 * `@graph` into <head> via useJsonLd. Mount once, high in the tree.
 */
export function SiteGraphJsonLd() {
  useJsonLd("site-graph", SITE_GRAPH);
  return null;
}
